// Shopify-app Privacy Policy — public, routed at /privacy-shopify.
//
// Scope: this policy is SPECIFIC to the Ad Astronaut Shopify app (write_products
// only; no customer/order/financial data). The general website policy lives at
// /privacy. Keep the two in sync when the stack or permissions change.
//
// ⚠️ Same pre-launch caveat as /privacy: accurate draft grounded in what the app
// does, but confirm the legal entity name, a contact email that resolves, and
// the sub-processor list before relying on it. Consider a Termly / Iubenda pass.

import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const LAST_UPDATED = 'August 14, 2026';
const CONTACT_EMAIL = 'privacy@adastronaut.com';

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="font-display text-xl md:text-2xl font-bold text-dark mb-3">{title}</h2>
      <div className="space-y-3 text-dark-muted text-sm md:text-base leading-relaxed">
        {children}
      </div>
    </section>
  );
}

const Scope = () => (
  <code className="rounded bg-surface-dark/50 px-1.5 py-0.5 font-mono text-[0.85em] text-dark">
    write_products
  </code>
);

const PROVIDERS: { name: string; purpose: string }[] = [
  { name: 'Google Cloud Platform', purpose: 'Hosting, database, and image storage' },
  { name: 'OpenAI', purpose: 'AI image and text generation' },
  { name: 'Google (Gemini)', purpose: 'AI image generation' },
  { name: 'Additional AI image providers', purpose: 'Certain rendering styles may use other AI image models' },
];

export default function PrivacyShopifyPage() {
  return (
    <div className="min-h-screen bg-surface flex flex-col">
      <Navbar />

      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-6 py-16 md:py-20">
          <div className="mb-10">
            <h1 className="font-display text-3xl md:text-4xl font-bold text-dark mb-2">
              Ad Astronaut for Shopify &mdash; Privacy Policy
            </h1>
            <p className="text-sm text-dark-muted">Last updated: {LAST_UPDATED}</p>
          </div>

          <div className="space-y-10">
            <Section id="intro" title="Introduction">
              <p>
                This Privacy Policy explains how Ad Astronaut (&ldquo;we&rdquo;, &ldquo;us&rdquo;,
                &ldquo;our&rdquo;) handles information in connection with the Ad Astronaut Shopify app
                (the &ldquo;App&rdquo;), which lets merchants generate styled product images from
                their existing product photos, directly inside the Shopify admin. It applies
                specifically to the Shopify app; our general website policy is available at{' '}
                <a href="/privacy" className="text-primary font-semibold hover:underline">
                  adastronaut.com/privacy
                </a>
                .
              </p>
            </Section>

            <Section id="collect" title="Information We Collect">
              <p>When you install and use the App, we collect:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  <span className="font-semibold text-dark">Store information</span> &mdash; your
                  store&rsquo;s myshopify.com domain and the Shopify session/access token needed to
                  operate the App. Installation is handled through Shopify OAuth; we never receive or
                  store a password.
                </li>
                <li>
                  <span className="font-semibold text-dark">Product content</span> &mdash; product
                  images, titles, descriptions, and category/type you choose to work with, retrieved
                  from your store via Shopify&rsquo;s Admin API (using the <Scope /> permission).
                </li>
                <li>
                  <span className="font-semibold text-dark">Generated content</span> &mdash; the
                  images the App creates for you, and related metadata (tool used, settings).
                </li>
                <li>
                  <span className="font-semibold text-dark">Billing state</span> &mdash; your
                  subscription plan and credit balance. Charges are handled by Shopify (see Billing);
                  we do not receive or store card numbers.
                </li>
                <li>
                  <span className="font-semibold text-dark">Diagnostic data</span> &mdash; basic
                  technical and error data used to keep the App reliable.
                </li>
              </ul>
              <p>
                We do not collect your customers&rsquo; personal information. The App requests only
                the <Scope /> permission and accesses no customer, order, or financial data.
              </p>
            </Section>

            <Section id="ai" title="AI Processing">
              <p>
                To generate your images, the App sends the product images you select and the prompts
                we construct to our rendering service and its third-party AI providers (see
                Sub-processors). These providers process that content solely to return the generated
                images. We do not use your content to train our own models. Please avoid uploading
                images containing sensitive personal information you do not want processed by these
                services.
              </p>
            </Section>

            <Section id="billing" title="Billing">
              <p>
                For the Shopify app, all subscription and credit charges are processed through
                Shopify&rsquo;s Billing API and appear in your Shopify invoice. We do not use any
                off-platform billing for the App.
              </p>
            </Section>

            <Section id="share" title="How We Share Information">
              <p>
                We do not sell your information. We share it only with the service providers below,
                strictly to operate the App:
              </p>
              <div className="mt-2 overflow-hidden rounded-xl border border-surface-dark/40">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-surface-dark/40 text-left">
                      <th className="px-4 py-2.5 font-semibold text-dark">Provider</th>
                      <th className="px-4 py-2.5 font-semibold text-dark">Purpose</th>
                    </tr>
                  </thead>
                  <tbody>
                    {PROVIDERS.map((p, i) => (
                      <tr key={p.name} className={i % 2 ? 'bg-white' : 'bg-surface/60'}>
                        <td className="px-4 py-2.5 font-medium text-dark align-top">{p.name}</td>
                        <td className="px-4 py-2.5 text-dark-muted align-top">{p.purpose}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p>
                We may also disclose information if required by law, to enforce our terms, or in
                connection with a business transfer.
              </p>
            </Section>

            <Section id="retention" title="Data Retention &amp; Deletion">
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  Generated images are kept in your App library so you don&rsquo;t lose what you paid
                  for. You can delete individual creatives at any time.
                </li>
                <li>
                  When you uninstall the App, we delete your session and purge your store&rsquo;s
                  generated-image library.
                </li>
                <li>
                  In line with Shopify&rsquo;s data-protection requirements, we honor Shopify&rsquo;s
                  shop/redact request (sent ~48 hours after uninstall) by erasing all remaining data
                  associated with your store.
                </li>
                <li>
                  You can also request deletion or export of your information at any time by
                  contacting{' '}
                  <a href={`mailto:${CONTACT_EMAIL}`} className="text-primary font-semibold hover:underline">
                    {CONTACT_EMAIL}
                  </a>
                  .
                </li>
              </ul>
            </Section>

            <Section id="security" title="Security">
              <p>
                We use encryption in transit, access controls, and reputable cloud infrastructure to
                protect your information. No method is completely secure, but we work to protect your
                data and promptly address issues.
              </p>
            </Section>

            <Section id="rights" title="Your Rights">
              <p>
                Depending on your location, you may have rights to access, correct, export, or delete
                your information (including under GDPR and CCPA). Exercise them by contacting{' '}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-primary font-semibold hover:underline">
                  {CONTACT_EMAIL}
                </a>
                . We will not discriminate against you for doing so.
              </p>
            </Section>

            <Section id="transfers" title="International Data Transfers">
              <p>
                We and our sub-processors may process information in countries other than yours,
                including the United States, with appropriate safeguards where required.
              </p>
            </Section>

            <Section id="changes" title="Changes to This Policy">
              <p>
                We may update this policy; we&rsquo;ll revise the &ldquo;Last updated&rdquo; date and
                provide notice where appropriate.
              </p>
            </Section>

            <Section id="contact" title="Contact">
              <p>
                Questions? Email{' '}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-primary font-semibold hover:underline">
                  {CONTACT_EMAIL}
                </a>
                .
              </p>
            </Section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
